/**
 * Posición y tamaño de los elementos del Diseñador, en milímetros reales de la tarjeta.
 *
 * Cada elemento del layout guarda x, y, width y height como PORCENTAJE del ancho/alto de la tarjeta (dos ejes
 * distintos, no la misma unidad). Este módulo es la única conversión entre esos porcentajes y los mm que ve y
 * escribe el usuario, más las reglas que mantienen al elemento dentro del lienzo. Son funciones puras (sin React)
 * para poder probarlas una por una; el editor y el panel de "Posición y tamaño" solo las llaman.
 *
 * Tarjeta: hoy TODOS los diseños usan la ISO/IEC 7810 ID-1 (85,6 × 53,98 mm). No existe un tamaño por diseño: el lienzo
 * (aspect-ratio en disenador.css), la vista previa, el recorte de las imágenes de fondo y la impresión (@page en
 * padron.css) lo tienen fijo. dimensionesTarjetaMm(diseno) es el único punto que habría que tocar el día que un municipio
 * pida otro tamaño (además de esas hojas de estilo y del backend).
 */

export const ANCHO_TARJETA_MM = 85.6;
export const ALTO_TARJETA_MM = 53.98;

// Mínimo que se puede ESCRIBIR en los campos de ancho y alto. A mano, a propósito, puede ser más chico que el mínimo
// del arrastre con el mouse (5 % del lienzo): una línea de 1,5 mm de grosor es legítima. El arrastre nunca agranda
// un elemento más chico que ese mínimo (ver alMoverMouse en DisenadorEditor.jsx).
export const MIN_MM = 1;
// Tope de lo que se acepta tipear, mucho más grande que la tarjeta: solo evita cosas absurdas antes de acotar al lienzo.
const MAX_TIPEO_MM = 1000;
// Tolerancia para decir "es el mismo valor" (se muestra con 2 decimales).
const EPS_MM = 0.005;

export const dimensionesTarjetaMm = () => ({ anchoMm: ANCHO_TARJETA_MM, altoMm: ALTO_TARJETA_MM });

// Proporción de la tarjeta (≈ 1,586). Un elemento con width% / height% == 1 NO es cuadrado en pantalla: para que lo
// sea hace falta height% = width% × este valor (así lo mantiene el QR).
export const ratioTarjeta = (dim = dimensionesTarjetaMm()) => dim.anchoMm / dim.altoMm;

const num = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
const redondear2 = (v) => Math.round(v * 100) / 100;

export const CAMPOS_MEDIDA = ['x', 'y', 'ancho', 'alto'];

// ---------- conversiones ----------
export const pctXAMm = (pct, dim = dimensionesTarjetaMm()) => (pct * dim.anchoMm) / 100;
export const pctYAMm = (pct, dim = dimensionesTarjetaMm()) => (pct * dim.altoMm) / 100;
export const mmAPctX = (mm, dim = dimensionesTarjetaMm()) => (mm / dim.anchoMm) * 100;
export const mmAPctY = (mm, dim = dimensionesTarjetaMm()) => (mm / dim.altoMm) * 100;

// ---------- texto <-> número ----------
// Acepta coma o punto como separador decimal ("12,5" y "12.5"), con o sin decimales ("12", "12.", ".5").
// Rechaza vacío, negativos, letras, notación científica y más de un separador (por ejemplo "1.234,5": ambiguo).
export function parsearMedida(texto) {
  const t = String(texto ?? '').trim().replace(/\s+/g, '');
  if (t === '') return { ok: false, motivo: 'vacio' };
  if (t.startsWith('-') || t.startsWith('−')) return { ok: false, motivo: 'negativo' };
  if (!/^(\d+([.,]\d*)?|[.,]\d+)$/.test(t)) return { ok: false, motivo: 'formato' };
  const valor = Number(t.replace(',', '.'));
  if (!Number.isFinite(valor)) return { ok: false, motivo: 'formato' };
  if (valor > MAX_TIPEO_MM) return { ok: false, motivo: 'enorme' };
  return { ok: true, valor };
}

export const MENSAJES_PARSEO = {
  vacio: 'Escribí un número en milímetros.',
  negativo: 'No se permiten valores negativos.',
  formato: 'Escribí solo números, con coma o punto para los decimales (por ejemplo 12,5).',
  enorme: 'Ese valor es demasiado grande para la tarjeta.',
};

// 12,5 / 12 / 0,33 — hasta 2 decimales, sin ceros de más, con coma (es-AR).
export function formatearMedida(mm) {
  if (!Number.isFinite(mm)) return '';
  return String(redondear2(mm)).replace('.', ',');
}

// ---------- geometría de un elemento ----------
// ¿Tiene ancho y alto propios? (un texto de ancho "automático" no los tiene: solo se le da posición).
export const tieneTamano = (el) => Number.isFinite(num(el?.width)) && Number.isFinite(num(el?.height));

export const esFoto = (el) => el?.tipo === 'imagen' && el?.fuente_imagen === 'foto_contribuyente';
export const esQR = (el) => el?.tipo === 'qr';

// Valores del elemento en mm: { x, y, ancho, alto } (ancho/alto = null si no tiene tamaño propio).
export function geometriaEnMm(el, dim = dimensionesTarjetaMm()) {
  const x = num(el.x); const y = num(el.y);
  const t = tieneTamano(el);
  return {
    x: Number.isFinite(x) ? pctXAMm(x, dim) : null,
    y: Number.isFinite(y) ? pctYAMm(y, dim) : null,
    ancho: t ? pctXAMm(num(el.width), dim) : null,
    alto: t ? pctYAMm(num(el.height), dim) : null,
  };
}

// Valor común de un campo entre varios elementos: { aplica, mixto, valorMm }.
//   aplica  → al menos un elemento tiene ese dato (los de ancho automático no tienen ancho ni alto);
//   mixto   → no todos coinciden (comparados a 2 decimales, como se muestran).
export function valorComunMm(elementos, campo, dim = dimensionesTarjetaMm()) {
  const valores = elementos
    .map((el) => geometriaEnMm(el, dim)[campo])
    .filter((v) => v !== null && Number.isFinite(v))
    .map(redondear2);
  if (valores.length === 0) return { aplica: false, mixto: false, valorMm: null };
  const mixto = valores.some((v) => Math.abs(v - valores[0]) > EPS_MM);
  return { aplica: true, mixto, valorMm: mixto ? null : valores[0] };
}

/**
 * Aplica UNA medida (en mm) a un elemento y devuelve el elemento nuevo, siempre dentro del lienzo:
 *   x, y        → entre 0 y lo que deje libre el elemento (x ≤ 100 % − ancho, y ≤ 100 % − alto);
 *   ancho, alto → desde MIN_MM hasta el borde del lienzo (según la posición actual: no se mueve x/y para hacer lugar);
 *   con mantenerProporcion, el otro lado acompaña manteniendo la proporción que el elemento tiene en ese momento
 *   (el QR siempre queda cuadrado, con o sin candado: un QR deformado no escanea).
 * Si el valor pedido no entra, se acota al máximo/mínimo posible y se informa (ajustado + motivo).
 * Un elemento sin ancho/alto propios solo acepta x e y; pedirle ancho/alto no hace nada.
 */
export function aplicarMedida(el, campo, mm, { dim = dimensionesTarjetaMm(), mantenerProporcion = false } = {}) {
  const sinCambio = { el, ajustado: false, motivo: null, valorFinalMm: null };
  if (!CAMPOS_MEDIDA.includes(campo) || !Number.isFinite(mm) || mm < 0) return sinCambio;

  const x0 = num(el.x); const y0 = num(el.y); const w0 = num(el.width); const h0 = num(el.height);
  const conTamano = Number.isFinite(w0) && Number.isFinite(h0);
  if ((campo === 'ancho' || campo === 'alto') && !conTamano) return sinCambio;
  if (!Number.isFinite(x0) || !Number.isFinite(y0)) return sinCambio;

  const bloqueo = conTamano && (mantenerProporcion || esQR(el));
  // Proporción h%/w% a conservar. QR: cuadrado de verdad (ancho = alto en mm).
  const r = esQR(el) ? dim.anchoMm / dim.altoMm : (conTamano && w0 > 0 ? h0 / w0 : 1);
  const minW = mmAPctX(MIN_MM, dim);
  const minH = mmAPctY(MIN_MM, dim);

  let x = x0; let y = y0; let w = w0; let h = h0;
  let pedidoPct; let finalPct; let limiteMax = null; let limiteMin = null;

  if (campo === 'x') {
    pedidoPct = mmAPctX(mm, dim);
    limiteMax = Math.max(0, conTamano ? 100 - w0 : 100);
    x = clamp(pedidoPct, 0, limiteMax); finalPct = x;
  } else if (campo === 'y') {
    pedidoPct = mmAPctY(mm, dim);
    limiteMax = Math.max(0, conTamano ? 100 - h0 : 100);
    y = clamp(pedidoPct, 0, limiteMax); finalPct = y;
  } else if (campo === 'ancho') {
    pedidoPct = mmAPctX(mm, dim);
    limiteMax = Math.min(100 - x0, bloqueo ? (100 - y0) / r : Infinity);
    limiteMin = Math.max(minW, bloqueo ? minH / r : 0);
    w = clamp(pedidoPct, limiteMin, Math.max(limiteMin, limiteMax)); finalPct = w;
    if (bloqueo) h = w * r;
  } else { // alto
    pedidoPct = mmAPctY(mm, dim);
    limiteMax = Math.min(100 - y0, bloqueo ? (100 - x0) * r : Infinity);
    limiteMin = Math.max(minH, bloqueo ? minW * r : 0);
    h = clamp(pedidoPct, limiteMin, Math.max(limiteMin, limiteMax)); finalPct = h;
    if (bloqueo) w = h / r;
  }

  const nuevo = { ...el, x, y };
  if (conTamano) { nuevo.width = w; nuevo.height = h; }

  const eje = campo === 'x' || campo === 'ancho' ? 'x' : 'y';
  const finalMm = eje === 'x' ? pctXAMm(finalPct, dim) : pctYAMm(finalPct, dim);
  const ajustado = Math.abs(finalMm - mm) > EPS_MM;
  let motivo = null;
  if (ajustado) {
    const fmt = formatearMedida(finalMm);
    if (mm > finalMm) {
      motivo = (campo === 'x' || campo === 'y')
        ? `Máximo ${fmt} mm: el elemento tiene que quedar dentro de la tarjeta.`
        : `Máximo ${fmt} mm con esta posición${bloqueo ? ' y la proporción bloqueada' : ''}. Para hacerlo más grande, movelo antes.`;
    } else {
      motivo = `Mínimo ${fmt} mm.`;
    }
  }
  return { el: nuevo, ajustado, motivo, valorFinalMm: finalMm };
}

/**
 * "Foto 3:4": deja la caja en proporción retrato 3 de ancho por 4 de alto (en milímetros reales), manteniendo el ancho.
 * Si el nuevo alto no entra debajo de la posición actual, sube la caja (y) lo necesario; si ni así entra
 * (ancho demasiado grande), reduce el ancho para que el alto sea el del lienzo completo.
 */
export function ajustarFoto34(el, dim = dimensionesTarjetaMm()) {
  const w0 = num(el.width); const y0 = num(el.y);
  if (!Number.isFinite(w0) || !Number.isFinite(y0) || !Number.isFinite(num(el.height))) return { el, ajustado: false, motivo: null };
  const factor = (dim.anchoMm / dim.altoMm) * (4 / 3); // alto% = ancho% × factor
  let w = w0; let y = y0; let h = w * factor;
  let motivo = null;
  if (y + h > 100 + 1e-9) {
    if (h <= 100) {
      y = 100 - h;
      motivo = 'La foto se subió un poco para que entre en la tarjeta (se mantuvo el ancho).';
    } else {
      h = 100; y = 0; w = h / factor;
      motivo = 'El ancho era demasiado grande para una foto 3:4 dentro de la tarjeta: se achicó para que entre.';
    }
  }
  return { el: { ...el, y, width: w, height: h }, ajustado: motivo !== null, motivo };
}

// ¿La caja ya está en 3:4 (a 0,01 mm)? Sirve para marcar el botón como "ya aplicado".
export function esFoto34(el, dim = dimensionesTarjetaMm()) {
  if (!tieneTamano(el)) return false;
  const { ancho, alto } = geometriaEnMm(el, dim);
  return Math.abs(redondear2(ancho * (4 / 3)) - redondear2(alto)) <= 0.015;
}

/**
 * Aplica una medida a varios elementos de una lista (los de `ids`) y devuelve { elementos, mensaje }.
 * Es lo que llama el Diseñador cuando se escribe un valor con uno o varios elementos seleccionados: cada elemento se acota
 * por su cuenta a lo que entra en la tarjeta. `mensaje` es el aviso a mostrar si algún valor se tuvo que acotar (o null).
 */
export function aplicarMedidaALista(elementos, ids, campo, mm, { dim = dimensionesTarjetaMm(), mantenerProporcion = false } = {}) {
  const sel = new Set(ids);
  let ajustados = 0;
  let primerMotivo = null;
  const nuevos = elementos.map((el) => {
    if (!sel.has(el.id)) return el;
    const r = aplicarMedida(el, campo, mm, { dim, mantenerProporcion });
    if (r.ajustado) { ajustados += 1; primerMotivo = primerMotivo || r.motivo; }
    return r.el;
  });
  let mensaje = null;
  if (ajustados > 0) {
    mensaje = sel.size === 1
      ? primerMotivo
      : `En ${ajustados} de ${sel.size} elementos el valor se acotó al máximo o mínimo posible para que queden dentro de la tarjeta.`;
  }
  return { elementos: nuevos, mensaje };
}

// "Foto 3:4" para las fotos del contribuyente de la selección (los demás elementos no se tocan).
export function aplicarFoto34ALista(elementos, ids, dim = dimensionesTarjetaMm()) {
  const sel = new Set(ids);
  const avisos = [];
  const nuevos = elementos.map((el) => {
    if (!sel.has(el.id) || !esFoto(el)) return el;
    const r = ajustarFoto34(el, dim);
    if (r.motivo) avisos.push(r.motivo);
    return r.el;
  });
  return { elementos: nuevos, mensaje: avisos.length ? avisos[0] : null };
}
